import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { PartRevisionView } from "src/api/generated/erp/db/types/tables/partRevisionView.ts";
import { dateColumn, enumColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPartRevisionStateLabels } from "src/api/generated/erp/db/types/enums/partRevisionState";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import {
	faBars,
	faCalculator,
	faEuroSign,
	faFileDownload,
	faShare,
	faSitemap,
	faStethoscope,
	faSyncAlt,
	faTable,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SitePartTestConfiguratorView } from "src/components/views/erp/configurator/configuratorTesting/sitePart/SitePartTestConfiguratorView.tsx";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import { SitePartRevisionsDataGridApi } from "src/api/generated/erp/parts/sitePart/api/sitePartRevisionsDataGridApi.ts";
import dayjs from "dayjs";
import { SitePartRevisionProductStructureView } from "src/components/views/erp/parts/siteParts/bom/SitePartRevisionProductStructureView.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { SitePartRevisionPriceConfiguratorForm } from "src/components/views/erp/parts/siteParts/SitePartRevisionPriceConfiguratorForm.tsx";
import { CopyPartRevisionBomForm } from "src/components/views/erp/parts/siteParts/bom/CopyPartRevisionBomForm.tsx";
import { SitePartRevisionBomTreeView } from "src/components/views/erp/parts/treeViews/SitePartRevisionBomTreeView.tsx";
import { PartRevisionCostCalculationLogDataGrid } from "src/components/views/erp/parts/siteParts/PartRevisionCostCalculationLogDataGrid.tsx";
import { SitePartRevisionForm } from "src/components/views/erp/parts/siteParts/SitePartRevisionForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SitePartRevisionsDataGridProps {
	partId: number;
	catalogPartRevisionId?: number;
	partViewRefreshRef?: RefreshableElementRef;
}

export const SitePartRevisionsDataGrid = ({
	partId,
	catalogPartRevisionId,
	partViewRefreshRef,
}: SitePartRevisionsDataGridProps) => {
	const showConfirm = useConfirmDialog();
	const showInputDialog = useInputDialog();
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			gridId={"58181636C73A69B3"}
			fetchData={() => SitePartRevisionsDataGridApi.getData({ partId, catalogPartRevisionId })}
			getRows={(data) => data.partRevisions}
			getRowId={(row) => row.partRevisionId}
			customFunctionUiActionView={"PART_REVISIONS"}
			initialParams={{}}
			selectFirstRowOnLoad
			render={({ data: { part }, dataGridProps, onlySelectedRow, refreshData }) => {
				return (
					<FormCrudDataGrid<PartRevisionView>
						columns={[
							textColumn({
								field: "revisionNumber",
								headerName: i18n.t("revision"),
								width: 60,
							}),
							enumColumn({
								field: "partRevisionState",
								headerName: i18n.t("state"),
								enumLabels: getPartRevisionStateLabels(),
							}),
							dateColumn({
								field: "validFrom",
								headerName: i18n.t("valid_from"),
								width: 125,
							}),
							textColumn({
								field: "note",
								headerName: i18n.t("comment"),
								width: 200,
							}),
							part.isConfigurable &&
								textColumn({
									field: "configurationProductFamilyVersionNumber",
									headerName: i18n.t("product_family_version"),
								}),
							dateColumn({
								field: "createdAt",
								headerName: i18n.t("created_at"),
							}),
							textColumn({
								field: "createdByUserName",
								headerName: i18n.t("created_by"),
								width: 120,
							}),
							dateColumn({
								field: "releasedAt",
								headerName: i18n.t("released_at"),
							}),
							textColumn({
								field: "releasedByUserName",
								headerName: i18n.t("released_by"),
								width: 120,
							}),
							textColumn({
								field: "materialCost",
								headerName: i18n.t("material_cost"),
								width: 140,
							}),
							textColumn({
								field: "workCost",
								headerName: i18n.t("labour_cost"),
							}),
							textColumn({
								field: "partRevisionId",
								headerName: i18n.t("id"),
							}),
						]}
						actionBarComponents={
							<>
								<DocumentsOfObjectButton
									objectRef={nullableAavoObjectRef("CATALOG_PART_REVISION", onlySelectedRow?.catalogPartRevisionId)}
								/>
								<AavoButton
									label={i18n.t("product_structure")}
									icon={faTable}
									variant={"outlined"}
									disabled={onlySelectedRow == null}
									onClick={() => {
										if (onlySelectedRow == null) return;
										openProductStructureView(onlySelectedRow);
									}}
								/>
							</>
						}
						rowContextMenuComponents={({ row, onlySingleRowSelected }) => [
							onlySingleRowSelected && (
								<>
									<AsyncMenuButton
										key={"importContent"}
										label={i18n.t("import_content")}
										icon={faFileDownload}
										onClick={async () => {
											openFormOnDialog({
												openDialog,
												title: i18n.t("import_content"),
												size: "sm",
												component: CopyPartRevisionBomForm,
												agentContext: {
													key: defineAgentUiContextKey("erp.parts.copyPartRevisionBom.formDialog"),
													title: i18n.t("import_content"),
													data: { targetPartRevisionId: row.partRevisionId },
												},
												props: {
													targetPartRevision: row,
												},
												confirmCloseIfEdited: false,
												onSubmit: refreshData,
											});
										}}
									/>
								</>
							),
							onlySingleRowSelected && row.partRevisionState === "DESIGNED" && (
								<AsyncMenuButton
									key={"release"}
									label={i18n.t("release")}
									icon={faShare}
									onClick={async () => {
										const validFrom = await showInputDialog({
											title: i18n.t("release"),
											type: "date",
											fieldLabel: i18n.t("valid_from"),
											required: true,
											defaultValue: dayjs(),
										});
										if (validFrom == null) return;
										await SitePartRevisionsDataGridApi.releaseRevision({
											partRevisionId: row.partRevisionId,
											validFrom: dayJsToDateIsoString(validFrom),
										});
										await refreshData();
									}}
								/>
							),
							onlySingleRowSelected && row.partRevisionState === "RELEASED" && (
								<AsyncMenuButton
									key={"unrelease"}
									label={i18n.t("revert_release")}
									icon={faUndo}
									onClick={async () => {
										const confirmed = await showConfirm({
											title: i18n.t("are_you_sure"),
											message: i18n.t("return_revision_to_initial"),
										});
										if (!confirmed) return;
										await SitePartRevisionsDataGridApi.revertReleasePartRevision({
											partRevisionId: row.partRevisionId,
										});
										await refreshData();
									}}
								/>
							),
							onlySingleRowSelected && (
								<AsyncMenuButton
									key={"calculateCosts"}
									label={i18n.t("calculate_costs")}
									icon={faCalculator}
									onClick={async () => {
										await SitePartRevisionsDataGridApi.calculatePartRevisionCosts({
											partRevisionId: row.partRevisionId,
										});
										await refreshData();
									}}
								/>
							),
							onlySingleRowSelected && (
								<AsyncMenuButton
									key={"syncCostToPart"}
									label={i18n.t("sync_cost_to_part")}
									icon={faSyncAlt}
									onClick={async () => {
										await SitePartRevisionsDataGridApi.updatePartStandardCost({
											partRevisionId: row.partRevisionId,
										});
										await refreshData();
										partViewRefreshRef?.refresh();
									}}
								/>
							),
							onlySingleRowSelected && (
								<AsyncMenuButton
									key={"productStructureTree"}
									label={i18n.t("product_structure_tree")}
									icon={faSitemap}
									onClick={() => {
										openDialog(() => ({
											title: `${i18n.t("product_structure")} (${row.partNo} 
												| ${row.partDescription_1} 
												| ${row.revisionNumber})`,
											agentContext: {
												key: defineAgentUiContextKey("erp.parts.sitePartRevisionBomTree.dialog"),
												title: i18n.t("product_structure_tree"),
												data: { partRevisionId: row.partRevisionId },
											},
											content: (
												<SitePartRevisionBomTreeView
													partRevisionId={row.partRevisionId}
												/>
											),
										}));
									}}
								/>
							),
							onlySingleRowSelected && (
								<AsyncMenuButton
									key={"costHistory"}
									label={i18n.t("cost_log")}
									icon={faBars}
									onClick={() => {
										openDialog(() => ({
											title: i18n.t("cost_calculation_log"),
											agentContext: {
												key: defineAgentUiContextKey("erp.parts.partRevisionCostCalculationLog.sitePartRevisionsDataGrid.dialog"),
												title: i18n.t("cost_calculation_log"),
												data: { partRevisionId: row.partRevisionId },
											},
											content: (
												<PartRevisionCostCalculationLogDataGrid
													partRevisionId={row.partRevisionId}
												/>
											),
										}));
									}}
								/>
							),
							part.isConfigurable && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"priceConfiguratorRules"}
									label={i18n.t("pricing_configurator")}
									icon={faEuroSign}
									onClick={async () => {
										openFormOnDialog({
											openDialog,
											component: SitePartRevisionPriceConfiguratorForm,
											title: i18n.t("pricing_configurator"),
											size: "xl",
											agentContext: {
												key: defineAgentUiContextKey("erp.parts.sitePartRevisionPriceConfigurator.formDialog"),
												title: i18n.t("pricing_configurator"),
												data: { partRevisionId: row.partRevisionId },
											},
											props: {
												partRevisionId: row.partRevisionId,
											},
										});
									}}
								/>
							),
							part.isConfigurable && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"testConfiguration"}
									label={i18n.t("test_configuration")}
									icon={faStethoscope}
									onClick={() => {
										openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
											title: i18n.t("test_configuration"),
											agentContext: {
												key: defineAgentUiContextKey("erp.parts.sitePartRevisionTestConfiguration.dialog"),
												title: i18n.t("test_configuration"),
												data: { partRevisionId: row.partRevisionId },
											},
											content: (
												<SitePartTestConfiguratorView
													sitePartRevisionId={row.partRevisionId}
													closeDialog={closeDialog}
													onDataDirtyStateChanged={onDataDirtyStateChanged}
												/>
											),
										}));
									}}
								/>
							),
						]}
						form={{
							editEnabled: true,
							addRowEnabled: false,
							dialogSize: "sm",
							dialogTitle: i18n.t("revision"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.parts.sitePartRevision.formDialog"),
								title: i18n.t("revision"),
								data: { partRevisionId: row?.partRevisionId ?? null },
							}),
							component: ({ row, onCompleted, onFormEdited }) => (
								<SitePartRevisionForm
									partRevisionId={row!.partRevisionId}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
								/>
							),
						}}
						onRowDoubleClick={async () => {
							if (onlySelectedRow == null) return;
							openProductStructureView(onlySelectedRow);
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.parts.sitePartRevisions.grid"),
							data: {
								partId,
								catalogPartRevisionId: catalogPartRevisionId ?? null,
							},
							rowFields: ["partRevisionId", "partId", "catalogPartRevisionId", "revisionNumber", "partRevisionState"],
							selectedRowPathSegment: (row) => `${i18n.t("revision")} ${row.revisionNumber}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);

	function openProductStructureView(revision: PartRevisionView) {
		openDialog(({ onDataDirtyStateChanged }) => ({
			title: i18n.t("product_structure"),
			agentContext: {
				key: defineAgentUiContextKey("erp.parts.sitePartRevisionProductStructure.dialog"),
				title: i18n.t("product_structure"),
				data: { partRevisionId: revision.partRevisionId },
			},
			content: (
				<SitePartRevisionProductStructureView
					partRevisionId={revision.partRevisionId}
					dataDirtyStateChanged={onDataDirtyStateChanged}
				/>
			),
		}));
	}
};
